import { z } from 'zod';
import { ETAPAS_PREPARACAO } from './estoque';

/** Parâmetros que cada loja ajusta em Ajustes › Parâmetros da loja. Vazio = padrão abaixo. */
export const parametrosLojaSchema = z.object({
  /** dias para os 3 níveis de alerta de carro parado (amarelo, laranja, vermelho) */
  alertasEstoqueDias: z
    .tuple([
      z.number().int().min(5).max(365),
      z.number().int().min(5).max(365),
      z.number().int().min(5).max(365),
    ])
    .refine(([a, b, c]) => a < b && b < c, 'Os dias precisam ser crescentes')
    .default([30, 60, 90]),
  /** etapas de preparação que a loja usa, na ordem dela */
  etapasPreparacao: z
    .array(z.enum(ETAPAS_PREPARACAO))
    .min(1, 'Escolha pelo menos uma etapa')
    .refine((l) => new Set(l).size === l.length, 'Etapa repetida')
    .default([...ETAPAS_PREPARACAO]),
  /** garantia padrão da loja (entra no recibo e nos contratos) */
  garantia: z
    .object({
      meses: z.number().int().min(0).max(36),
      km: z.number().int().min(0).max(100_000),
    })
    .default({ meses: 3, km: 3_000 }),
  /** avisa quando o preço deixa a margem abaixo disto (%) */
  margemMinimaPercentual: z.number().min(0).max(100).default(8),
  /** crediário: valores que já vêm preenchidos (centésimos de ponto: 199 = 1,99%) */
  crediario: z
    .object({
      jurosMensal: z.number().int().min(0).max(1_000),
      multaAtraso: z.number().int().min(0).max(200), // CDC: até 2%
      moraMensal: z.number().int().min(0).max(100), // até 1% ao mês
    })
    .default({ jurosMensal: 0, multaAtraso: 200, moraMensal: 100 }),
  /** textos que saem nos documentos */
  textos: z
    .object({
      rodapeRecibo: z.string().trim().max(500),
      dadosBancarios: z.string().trim().max(300),
      observacoesContrato: z.string().trim().max(2_000),
    })
    .default({ rodapeRecibo: '', dadosBancarios: '', observacoesContrato: '' }),
});
export type ParametrosLoja = z.infer<typeof parametrosLojaSchema>;
export const PARAMETROS_PADRAO: ParametrosLoja = parametrosLojaSchema.parse({});

/** Lê o que está salvo; o que faltar ou estiver inválido volta ao padrão (nunca quebra a loja). */
export function lerParametros(salvo: unknown): ParametrosLoja {
  const base = (salvo && typeof salvo === 'object' ? salvo : {}) as Record<string, unknown>;
  const r: Record<string, unknown> = {};
  for (const k of Object.keys(PARAMETROS_PADRAO) as (keyof ParametrosLoja)[]) {
    const um = parametrosLojaSchema.shape[k].safeParse(base[k]);
    r[k] = um.success ? um.data : PARAMETROS_PADRAO[k];
  }
  return r as ParametrosLoja;
}

/** Nível do alerta de carro parado (0 a 3) e o limite de dias que ele passou. */
export function alertaEstoque(
  dias: number,
  limites: readonly [number, number, number] = PARAMETROS_PADRAO.alertasEstoqueDias,
): { nivel: 0 | 1 | 2 | 3; limite: number | null } {
  if (dias >= limites[2]) return { nivel: 3, limite: limites[2] };
  if (dias >= limites[1]) return { nivel: 2, limite: limites[1] };
  if (dias >= limites[0]) return { nivel: 1, limite: limites[0] };
  return { nivel: 0, limite: null };
}

/** "3 meses ou 3.000 km, o que vencer primeiro" (km 0 = só meses). */
export function textoGarantia(g: { meses: number; km: number }): string {
  const meses = `${g.meses} ${g.meses === 1 ? 'mês' : 'meses'}`;
  return g.km > 0 ? `${meses} ou ${g.km.toLocaleString('pt-BR')} km, o que vencer primeiro` : meses;
}
